import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PopoverPanel } from './popover';

// ============================================================================
// Combobox — the form-controls standard for a LONG list (form-controls.md
// "Long list: A"): a real text field; start typing and the list narrows;
// Enter takes the top match; ↑/↓ move; Escape closes. A second line
// (`sub`) rides along for job numbers, phone, unit size. Replaces
// <select> with 7+ options (clients, crew, materials, tags).
//
// items: [{ value, label, sub?, color?, disabled? }]
// value / onChange(value)  — value is the item's `value` ('' when cleared)
// allowCustom: onChange(typedText) on blur/Enter when nothing matches
//   (room tags, custom sizes). Default false: unknown text reverts.
// placeholder, size ('md' | 'sm'), disabled, ariaLabel, className, clearable.
// ============================================================================

const norm = (s) => String(s ?? '').toLowerCase();

export default function Combobox({
    items = [], value, onChange, placeholder = 'Type to find…', size = 'md', disabled = false,
    allowCustom = false, clearable = true, ariaLabel, className = '', inputClassName = '', maxResults = 50, autoFocus,
}) {
    const selected = useMemo(() => items.find(i => String(i.value) === String(value ?? '')) || null, [items, value]);
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(selected ? selected.label : (allowCustom && value ? String(value) : ''));
    const [hi, setHi] = useState(0);
    const anchorRef = useRef(null);
    const listRef = useRef(null);
    const typingRef = useRef(false);
    const close = useCallback(() => setOpen(false), []);

    // Outside change of value → refresh the shown text (unless typing).
    useEffect(() => {
        if (typingRef.current) return;
        setText(selected ? selected.label : (allowCustom && value ? String(value) : ''));
    }, [selected, value, allowCustom]);

    const q = norm(text);
    // Until the user types, show the whole list (with the current pick
    // marked) — opening on focus shouldn't filter by the label already there.
    const showAll = !typingRef.current;
    const filtered = useMemo(() => {
        const base = showAll || !q ? items : items.filter(i => norm(i.label).includes(q) || norm(i.sub).includes(q));
        return base.slice(0, maxResults);
    }, [items, q, showAll, maxResults]);

    useEffect(() => { setHi(0); }, [q, open]);
    useEffect(() => {
        if (!open) return;
        const el = listRef.current?.querySelector(`[data-idx="${hi}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [hi, open]);

    const commit = (it) => {
        typingRef.current = false;
        if (it) { setText(it.label); onChange?.(it.value); }
        close();
    };
    const commitText = () => {
        typingRef.current = false;
        const exact = items.find(i => norm(i.label) === q);
        if (exact) { commit(exact); return; }
        if (allowCustom) { onChange?.(text.trim()); close(); return; }
        if (!text.trim()) { onChange?.(''); close(); return; }
        // Unknown text and customs not allowed → revert to the current pick.
        setText(selected ? selected.label : '');
        close();
    };

    const h = size === 'sm' ? 'h-8 text-[13px] px-2.5' : 'h-10 text-sm px-3';

    return (
        <>
            <div ref={anchorRef} className={`relative ${className}`}>
                <input
                    type="text"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={open}
                    aria-label={ariaLabel}
                    autoComplete="off"
                    autoFocus={autoFocus}
                    disabled={disabled}
                    value={text}
                    placeholder={placeholder}
                    onFocus={e => { e.target.select(); typingRef.current = false; setOpen(true); }}
                    onChange={e => { typingRef.current = true; setText(e.target.value); setOpen(true); }}
                    onBlur={() => {
                        // Let a click on a list item land first (mousedown
                        // on the portaled list doesn't blur-commit).
                        setTimeout(() => { if (typingRef.current) commitText(); }, 0);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(i => Math.min(filtered.length - 1, i + 1)); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(i => Math.max(0, i - 1)); }
                        else if (e.key === 'Enter') { e.preventDefault(); if (open && filtered[hi] && (typingRef.current || !selected)) commit(filtered[hi]); else commitText(); }
                        else if (e.key === 'Escape') { typingRef.current = false; setText(selected ? selected.label : ''); close(); }
                    }}
                    className={`w-full ${h} pr-8 rounded-lg border border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed ${inputClassName}`}
                />
                {clearable && !disabled && (selected || (allowCustom && text)) ? (
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Clear"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { typingRef.current = false; setText(''); onChange?.(''); setOpen(false); }}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 text-[15px] leading-none"
                    >×</button>
                ) : (
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[11px] pointer-events-none" aria-hidden="true">▾</span>
                )}
            </div>
            <PopoverPanel open={open && !disabled} onClose={close} anchorRef={anchorRef} width="anchor" role="listbox" ariaLabel={ariaLabel} className="p-1.5">
                <div ref={listRef} className="max-h-[264px] overflow-auto">
                    {filtered.length === 0 && (
                        <div className="px-2.5 py-2 text-[13px] text-slate-400">{allowCustom && text ? `Press Enter to use “${text}”` : 'No matches — check spelling'}</div>
                    )}
                    {filtered.map((it, idx) => {
                        const on = selected && String(it.value) === String(selected.value);
                        return (
                            <button
                                key={String(it.value)}
                                type="button"
                                role="option"
                                aria-selected={on}
                                data-idx={idx}
                                disabled={it.disabled}
                                onMouseDown={e => e.preventDefault()}
                                onMouseEnter={() => setHi(idx)}
                                onClick={() => commit(it)}
                                className={`w-full flex items-center gap-2.5 text-left rounded-lg px-2.5 min-h-[40px] py-1.5 text-sm transition-colors ${
                                    idx === hi ? 'bg-slate-100' : ''
                                } ${on ? 'font-semibold text-slate-900' : 'text-slate-800'} disabled:opacity-40`}
                            >
                                {it.color && <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: it.color }} />}
                                <span className="flex-1 min-w-0 truncate">{it.label}</span>
                                {it.sub && <span className="font-mono text-[11px] text-slate-400 flex-shrink-0">{it.sub}</span>}
                                {on && <span className="text-red-600 font-bold flex-shrink-0" aria-hidden="true">✓</span>}
                            </button>
                        );
                    })}
                </div>
            </PopoverPanel>
        </>
    );
}
